import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { useMemo } from "react";
import { UploadFileMetadataOutput } from "@vendor-app/app/_sharedComponents/FileImports/libs/types";
import { bulkUpdatesEndpointsMap } from "../../libs/utils/constants";

export interface BulkUpdatesTable {
  loading: boolean;
  error: string | null;
  files: UploadFileMetadataOutput[];

  actions: {
    fetchFiles(bulkActionType: string): Promise<void>;
    commitFileById(id: string, bulkActionType: string): Promise<void>;
    downloadFeedback(id: string, type: "pre-commit" | "commit"): Promise<void>;
    downloadUploaded(id: string, name: string): Promise<void>;
    clearError(): void;
  };
}

const bulkUpdatesTableSlice: StateCreator<BulkUpdatesTable, [["zustand/immer", never]]> = (set, get) => ({
  loading: false,
  error: null,
  files: [],

  actions: {
    async fetchFiles(bulkActionType) {
      set(draft => {
        draft.loading = true;
        draft.error = null;
      });

      const response = await typedFrontendVendorApi.getReq(bulkUpdatesEndpointsMap[bulkActionType]);

      if (response.type === "error") {
        set(draft => {
          draft.loading = false;
          draft.error = response.message ?? "Failed to fetch files.";
        });
        return;
      }

      set(draft => {
        draft.loading = false;
        draft.files = response.body;
      });
    },

    async commitFileById(id, bulkActionType) {
      const response = await typedFrontendVendorApi.postReq(
        `${bulkUpdatesEndpointsMap[bulkActionType]}/${id}/commit`
      );

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Commit failed";
        });
      }

      await get().actions.fetchFiles(bulkActionType);
    },

    async downloadFeedback(id, type) {
      const endpoint =
        type === "pre-commit" ? `/bulk-actions/${id}/pre-commit-feedback` : `/bulk-actions/${id}/commit-feedback`;

      const response = await typedFrontendVendorApi.getReq(endpoint);

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Download failed";
        });
      }

      if (response.type === "success") {
        const blob = new Blob([response.body], { type: "text/csv;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${type}-feedback.csv`;
        link.click();
      }
    },

    async downloadUploaded(id, name) {
      const endpoint = `/bulk-actions/${id}/uploaded-file`;

      const response = await typedFrontendVendorApi.getReq(endpoint);

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Download failed";
        });
      }

      if (response.type === "success") {
        const blob = new Blob([response.body], { type: "text/csv;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${name}`;
        link.click();
      }
    },

    clearError: () => set({ error: null }),
  },
});

const useBulkUpdatesTable = create<BulkUpdatesTable>()(immer(bulkUpdatesTableSlice));

const initialStoreState = useBulkUpdatesTable.getState();

export const BulkUpdatesTableAccess = {
  useActions() {
    return useBulkUpdatesTable(state => state.actions);
  },

  useFiles() {
    return useBulkUpdatesTable(state => state.files);
  },

  useLoading() {
    return useBulkUpdatesTable(state => state.loading);
  },

  useError() {
    return useBulkUpdatesTable(state => state.error);
  },

  useHasFiles() {
    const files = BulkUpdatesTableAccess.useFiles();
    return useMemo(() => files.length > 0, [files]);
  },

  testing: {
    store: useBulkUpdatesTable,
    actions() {
      return useBulkUpdatesTable.getState().actions;
    },
    reset() {
      return useBulkUpdatesTable.setState(initialStoreState);
    },
  },
};
